import {Typography} from '@material-ui/core';
import {Skeleton} from '@material-ui/lab';
import {Async} from 'Core/models/Async';
import {OpportunitySummaryDto} from 'Quoting/models/DTOs/Opportunity';
import {safeRenderValue} from 'Quoting/utils/utilities';
import {renderAsyncView} from 'Shared/components/RenderAsync';
import React from 'react';
import {GridHeader, GridHeaderLoader} from '../shared/GridHeader';
import {QuotingCard} from '../shared/QuotingCard/QuotingCard';

const opportunityDetailsLabelsMap = {
  status: 'Status',
  sicCode: 'SIC Code',
  census: 'Census',
  salesRep: 'Sales Rep',
  brokerOfRecord: 'Broker of Record',
  servicingAgent: 'Servicing Agent',
  products: 'Products',
};

const mapOpportunitDetailsToView = (opportunitySummary: OpportunitySummaryDto, products: Set<string>) => {
  const {state, sicCode, leadSalesRep, brokerOfRecord, servicingAgent} = opportunitySummary;
  if (Array.isArray(opportunitySummary.products)) {
    opportunitySummary.products.forEach(({productCd}) => products.add(productCd));
  }

  return {
    status: safeRenderValue(state, true),
    products: safeRenderValue(Array.from(products).join(', ')),
    sicCode: safeRenderValue(sicCode, true),
    census: 'No Census',
    salesRep: safeRenderValue(`${leadSalesRep?.firstName} ${leadSalesRep?.lastName}`, true),
    brokerOfRecord: safeRenderValue(`${brokerOfRecord?.legalName} (${brokerOfRecord?.producerStatus})`),
    servicingAgent: safeRenderValue(`${servicingAgent?.legalName}`),
  };
};

export const OpportunitySummaryLoading = () => <GridHeaderLoader />;

type OpportunitySummaryHeaderProps = {
  opportunitySummary: Async<OpportunitySummaryDto>;
  products: Set<string>;
  opportunityId: string;
};

const OpportunitySummaryCardTitle = ({
  opportunitySummary,
  opportunityId,
}: {
  opportunitySummary: Async<OpportunitySummaryDto>;
  opportunityId: string;
}) => {
  return (
    <>
      <Typography component="h1">Opportunity Case: #{opportunityId}</Typography>

      <Typography component="h2">
        <span>Customer Name: </span>
        <span>
          {renderAsyncView(
            opportunitySummary,
            ({customerName}) => (
              <>{safeRenderValue(customerName)}</>
            ),
            () => (
              <Skeleton style={{display: 'inline-block'}} width={100} />
            )
          )}
        </span>
      </Typography>
    </>
  );
};

export const OpportunitySummaryHeader = React.memo(
  ({opportunitySummary, products, opportunityId}: OpportunitySummaryHeaderProps) => {
    return (
      <QuotingCard
        title={<OpportunitySummaryCardTitle opportunityId={opportunityId} opportunitySummary={opportunitySummary} />}
        ariaLandmarkName="Opportunity Summary"
      >
        {renderAsyncView(
          opportunitySummary,
          opportunityDetails => (
            <GridHeader
              data={mapOpportunitDetailsToView(opportunityDetails, products)}
              labelMapping={opportunityDetailsLabelsMap}
            />
          ),
          OpportunitySummaryLoading
        )}
      </QuotingCard>
    );
  }
);
